import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { foldersApi, templatesApi } from '@/lib/api'
import type { Folder } from '@/lib/api/folders'
import type { Template } from '@/types/domain'

/**
 * Everything the Projekte screen reads and writes.
 *
 * Two lists and nothing else: the gateway has no per-folder counts, no folder
 * search and no endpoint that returns a project with its forms, so the screen
 * loads both and joins them (`buildProjects`). That is right at today's sizes
 * and wrong at scale — reported.
 */

export const FOLDERS_KEY = ['folders'] as const
/** Shared with the Übersicht's listing, so a move shows up on both. */
/**
 * Distinct from the Übersicht's `['templates', 'overview']`, and deliberately
 * so: that one is a `useInfiniteQuery`, whose cache entry is
 * `{pages, pageParams}`, while this is a plain list. Sharing the bare
 * `['templates']` key meant whichever screen mounted first decided the shape,
 * so opening a project from the Übersicht crashed this page with "templates is
 * not iterable" while loading /projekte directly worked. Both still begin with
 * `'templates'`, so every `invalidateQueries({queryKey: ['templates']})`
 * across the app still matches both by prefix.
 */
export const TEMPLATES_KEY = ['templates', 'all'] as const

/**
 * The gateway's ceiling (`page_size` max 100, default 20). Anything past it
 * is invisible to this screen *and* to its search box, so the page says so
 * rather than quietly showing a hundred of four hundred forms.
 */
export const TEMPLATE_PAGE_SIZE = 100

export function useFolders() {
  return useQuery({ queryKey: FOLDERS_KEY, queryFn: () => foldersApi.list() })
}

export function useTemplates() {
  return useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: () => templatesApi.list({ archived: false, pageSize: TEMPLATE_PAGE_SIZE }),
  })
}

export function useProjectMutations() {
  const queryClient = useQueryClient()
  const invalidateFolders = () => queryClient.invalidateQueries({ queryKey: FOLDERS_KEY })
  const invalidateBoth = () => {
    void invalidateFolders()
    void queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY })
  }

  const create = useMutation({
    mutationFn: (name: string): Promise<Folder> => foldersApi.create(name.trim()),
    onSuccess: invalidateFolders,
  })

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      foldersApi.patch(id, { name: name.trim() }),
    onSuccess: invalidateFolders,
  })

  // Deleting a project does not delete its forms: `form_documents.folder_id`
  // is `ON DELETE SET NULL`, so they reappear under "Ohne Projekt". Both
  // lists therefore have to be refetched, not just the folders.
  const remove = useMutation({
    mutationFn: (id: string) => foldersApi.remove(id),
    onSuccess: invalidateBoth,
  })

  const move = useMutation({
    mutationFn: ({ templateId, folderId }: { templateId: string; folderId: string }) =>
      templatesApi.setFolder(templateId, folderId),
    onSuccess: invalidateBoth,
  })

  return { create, rename, remove, move }
}

export type { Folder, Template }
